import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Animated, Platform } from 'react-native';
import { globalFontStyle } from '../../../assets';
import Colors from '../../../assets/Color/Colors';
import { globalConfigurableData } from '../../../utils/globalConstant';
import ReactDOM from 'react-dom';

const ChevronIcon = ({ isUp }) => (
  <View style={[styles.chevron, isUp && styles.chevronUp]}>
    <View style={styles.chevronLine1} />
    <View style={styles.chevronLine2} />
  </View>
);

const Select = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  error,
  disabled = false,
  fullWidth = true,
  required = false,
  variant = 'outline',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [animation] = useState(new Animated.Value(0));
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectButtonRef = useRef(null);

  const selectedOption = options.find(option => option.value === value);

  // Add portal container for web dropdown
  const [portalContainer] = useState(() => {
    if (Platform.OS === 'web') {
      const div = document.createElement('div');
      div.style.position = 'fixed';
      div.style.zIndex = '2147483647'; // Same as Toast
      return div;
    }
    return null;
  });

  useEffect(() => {
    if (isOpen) {
      Animated.timing(animation, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(animation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [isOpen]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web' && portalContainer) {
      document.body.appendChild(portalContainer);
      return () => {
        document.body.removeChild(portalContainer);
      };
    }
  }, [portalContainer]);

  const updateDropdownPosition = () => {
    if (Platform.OS === 'web' && selectButtonRef.current) {
      const rect = selectButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (isOpen && Platform.OS === 'web') {
      updateDropdownPosition();
      // Update position on scroll and resize
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);

      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        updateDropdownPosition();
      }
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  return (
    <View 
      ref={Platform.OS === 'web' ? dropdownRef : undefined}
      style={[
        styles.container,
        fullWidth && styles.fullWidth,
        disabled && styles.disabled
      ]}
    >
      {label && (
        <Text style={styles.label}>{label}{required && ' *'}</Text>
      )}
      
      <TouchableOpacity
        ref={selectButtonRef}
        onPress={toggleDropdown}
        style={[
          styles.selectButton,
          variant === 'outline' && styles.outlineVariant,
          error && styles.error,
          isOpen && styles.focused,
          disabled && styles.disabledButton
        ]}
      >
        <Text 
          style={[
            styles.selectedText,
            !selectedOption && styles.placeholder
          ]}
          numberOfLines={1}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </Text>
        <ChevronIcon isUp={isOpen} />
      </TouchableOpacity>

      {isOpen && (
        Platform.OS === 'web' 
          ? ReactDOM.createPortal(
              <Animated.View 
                style={[
                  styles.dropdown,
                  {
                    transform: [{
                      translateY: animation.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-20, 0],
                      })
                    }],
                    opacity: animation,
                    position: 'absolute',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                  }
                ]}
              >
                <ScrollView style={styles.optionsContainer}>
                  {options.map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        styles.option,
                        option.value === value && styles.selectedOption,
                      ]}
                      onPress={() => handleSelect(option)}
                    >
                      <Text
                        style={[
                          styles.optionText,
                          option.value === value && styles.selectedOptionText,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </Animated.View>,
              portalContainer
            )
          : <Animated.View style={[styles.dropdown, {
              transform: [{
                translateY: animation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-20, 0],
                })
              }],
              opacity: animation
            }]}>
              <ScrollView 
                style={styles.optionsContainer}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                {options.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    style={[
                      styles.option,
                      option.value === value && styles.selectedOption
                    ]}
                    onPress={() => handleSelect(option)}
                  >
                    <Text 
                      style={[
                        styles.optionText,
                        option.value === value && styles.selectedOptionText
                      ]}
                      numberOfLines={1}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </Animated.View>
      )}

      {error && (
        <Text style={styles.errorText}>{error}</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    marginBottom: 16,
  },
  fullWidth: {
    width: '100%',
  },
  disabled: {
    opacity: 0.6,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed',
    }),
  },
  label: {
    ...globalFontStyle.appFontBody,
    fontSize: 13,
    color: globalConfigurableData.secondaryTextColor1,
    marginBottom: 4,
    opacity: 0.8,
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 46,
    height: 46,
    paddingHorizontal: 12,
    backgroundColor: globalConfigurableData.cardBgColor,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: globalConfigurableData.borderColor,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  outlineVariant: {
    borderWidth: 1,
    borderColor: globalConfigurableData.borderColor,
  },
  error: {
    borderColor: Colors.red,
    borderWidth: 1,
  },
  focused: {
    borderColor: Colors.tradexBlue,
    borderWidth: 1,
  },
  disabledButton: {
    backgroundColor: globalConfigurableData.cardBgColor,
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed',
    }),
  },
  selectedText: {
    ...globalFontStyle.appFontBody,
    color: globalConfigurableData.primaryTextColor1,
    fontSize: 14,
    flex: 1,
    marginRight: 8,
    lineHeight: 20,
  },
  placeholder: {
    color: globalConfigurableData.secondaryTextColor1,
    opacity: 0.5,
  },
  chevron: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chevronLine1: {
    position: 'absolute',
    width: 8,
    height: 2,
    backgroundColor: globalConfigurableData.secondaryTextColor1,
    transform: [{ rotate: '45deg' }],
    left: 4,
  },
  chevronLine2: {
    position: 'absolute',
    width: 8,
    height: 2,
    backgroundColor: globalConfigurableData.secondaryTextColor1,
    transform: [{ rotate: '-45deg' }],
    right: 4,
  },
  chevronUp: {
    transform: [{ rotate: '180deg' }],
  },
  dropdown: {
    backgroundColor: globalConfigurableData.cardBgColor,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: globalConfigurableData.borderColor,
    shadowColor: 'rgba(0, 0, 0, 0.1)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 4,
    zIndex: Platform.OS === 'web' ? 2147483647 : 1000,
    ...(Platform.OS === 'web' ? {
      position: 'absolute',
    } : {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 4,
    }),
  },
  optionsContainer: {
    maxHeight: 200,
  },
  option: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: globalConfigurableData.borderColor,
    minHeight: 46,
  },
  selectedOption: {
    backgroundColor: globalConfigurableData.primaryTextColor1 + '10',
  },
  optionText: {
    ...globalFontStyle.appFontBody,
    color: globalConfigurableData.primaryTextColor1,
    fontSize: 14,
    lineHeight: 20,
  },
  selectedOptionText: {
    color: globalConfigurableData.primaryTextColor1,
    fontWeight: '500',
  },
  errorText: {
    ...globalFontStyle.appFontSmallText,
    color: Colors.red,
    marginTop: 4,
    marginLeft: 2,
  },
});

export default Select; 